import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { InternshipPhase } from '../components/InternshipDashboard/InternshipDashboardV2';
import { PhaseGuidance } from './usePhaseGuidance';

export type AllPhaseGuidance = Record<InternshipPhase, PhaseGuidance>;

const PHASES: InternshipPhase[] = ['onboarding', 'foundation', 'growth', 'impact', 'transition'];

const FALLBACK_GUIDANCE: AllPhaseGuidance = {
  onboarding: {
    tips: [
      { title: 'Be proactive', description: 'Take initiative in your onboarding' },
      { title: 'Ask questions', description: 'Don\'t hesitate to seek clarification' },
      { title: 'Take notes', description: 'Document everything you learn' }
    ],
    focus_areas: ['Learning systems', 'Meeting team', 'Understanding culture'],
    common_mistakes: ['Being passive', 'Not asking questions', 'Information overload']
  },
  foundation: {
    tips: [
      { title: 'Build relationships', description: 'Connect with your team members' },
      { title: 'Learn the codebase', description: 'Understand the architecture' },
      { title: 'Start small', description: 'Begin with manageable tasks' }
    ],
    focus_areas: ['Technical foundations', 'Team dynamics', 'Process understanding'],
    common_mistakes: ['Taking on too much', 'Working in isolation', 'Not seeking feedback']
  },
  growth: {
    tips: [
      { title: 'Take ownership', description: 'Own your projects end-to-end' },
      { title: 'Seek feedback', description: 'Actively request code reviews' },
      { title: 'Contribute ideas', description: 'Share your perspectives in meetings' }
    ],
    focus_areas: ['Skill development', 'Project ownership', 'Impact creation'],
    common_mistakes: ['Avoiding challenges', 'Not communicating blockers', 'Lack of initiative']
  },
  impact: {
    tips: [
      { title: 'Drive results', description: 'Focus on measurable outcomes' },
      { title: 'Mentor others', description: 'Help newer team members' },
      { title: 'Think strategically', description: 'Consider long-term implications' }
    ],
    focus_areas: ['Leadership', 'Strategic thinking', 'Measurable impact'],
    common_mistakes: ['Short-term thinking', 'Not documenting work', 'Avoiding visibility']
  },
  transition: {
    tips: [
      { title: 'Document everything', description: 'Create comprehensive handoff docs' },
      { title: 'Reflect on growth', description: 'Assess your journey and learnings' },
      { title: 'Network actively', description: 'Strengthen professional connections' }
    ],
    focus_areas: ['Knowledge transfer', 'Reflection', 'Future planning'],
    common_mistakes: ['Poor handoff', 'Not maintaining connections', 'Forgetting to reflect']
  }
};

export function useAllPhaseGuidance(industryType: string | null | undefined) {
  const [guidance, setGuidance] = useState<AllPhaseGuidance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchAllGuidance = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use industry type if available, otherwise fall back to generic
        const typeToFetch = industryType || 'generic';

        // Fetch all phases at once
        const { data, error: fetchError } = await supabase
          .from('phase_guidance_templates')
          .select('phase, tips, focus_areas, common_mistakes')
          .eq('industry_type', typeToFetch)
          .in('phase', PHASES);

        if (fetchError) {
          throw fetchError;
        }

        // If we got less than 5 phases, try to fill in with generic
        if (!data || data.length < 5) {
          const missingPhases = PHASES.filter(
            phase => !data?.find(d => d.phase === phase)
          );

          if (missingPhases.length > 0 && typeToFetch !== 'generic') {
            const { data: genericData, error: genericError } = await supabase
              .from('phase_guidance_templates')
              .select('phase, tips, focus_areas, common_mistakes')
              .eq('industry_type', 'generic')
              .in('phase', missingPhases);

            if (!genericError && genericData) {
              data?.push(...genericData);
            }
          }
        }

        // Convert array to record
        const guidanceMap: AllPhaseGuidance = { ...FALLBACK_GUIDANCE };
        
        data?.forEach(item => {
          guidanceMap[item.phase as InternshipPhase] = {
            tips: item.tips,
            focus_areas: item.focus_areas,
            common_mistakes: item.common_mistakes
          };
        });

        setGuidance(guidanceMap);
      } catch (err) {
        console.error('Error fetching all phase guidance:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch guidance'));
        // Use fallback guidance
        setGuidance(FALLBACK_GUIDANCE);
      } finally {
        setLoading(false);
      }
    };

    fetchAllGuidance();
  }, [industryType]);

  return { guidance, loading, error };
}